import {
  InsightDetail,
  InsightTrendGroupByWords,
  InsightTrendWords,
  InsightTrendGroupByDetail,
  InsightWords,
  InsightTrendDetail,
} from './types';
import {
  ComputationKind,
  Direction,
  Insight,
  InsightKind,
  TrendGroupDetail,
} from '@fullstory/proto/fs/api/proactiveinsights';
import { hashObject } from '@fullstory/util/hash';
import { intl } from '@fullstory/intl';
import FeatureUsed = TrendGroupDetail.FeatureUsed;

export const insightToInsightDetail = (insight: Insight) => {
  const insightWords = getInsightWords(insight);
  return {
    ...insight,
    dxIdObjectHash: hashObject(insight.dxObject),
    insightWords,
    title: getInsightTitle(insightWords, insight),
  };
};

export const mapInsightResponseToInsightDetail = (insights: Insight[]): InsightDetail[] =>
  insights.map(insightToInsightDetail);

export const getInsightWords = (insight: Insight): InsightWords => {
  switch (insight.computationKind.valueOf()) {
    case ComputationKind.COMPUTATION_KIND_TREND:
      return getTrendInsightWords(insight);
    case ComputationKind.COMPUTATION_KIND_TREND_DIMENSIONALITY:
      return {
        topValueIsIncrease:
          insight.trendGroup?.topValueChange?.direction === Direction.DIRECTION_INCREASING,
        topValueRatioChange: getRatioChangeToDisplay(
          insight.trendGroup?.topValueChange?.ratioChange,
        ),
        gapValueIsIncrease:
          insight.trendGroup?.gapChange?.direction === Direction.DIRECTION_INCREASING,
        gapValueRatioChange: getRatioChangeToDisplay(insight.trendGroup?.gapChange?.ratioChange),
      };
    default:
      return getTrendInsightWords(insight);
  }
};

const getTrendInsightWords = (insight: Insight) => ({
  isIncrease: insight.trend?.direction === Direction.DIRECTION_INCREASING,
  isMonthly: insight.kind === InsightKind.INSIGHT_KIND_MONTH_OVER_MONTH,
  isVolatilityTrend: isVol(insight),
  ratioChangeI18l: getRatioChangeToDisplay(insight.trend?.ratioChange),
  numPreviousWeeks: (insight.trend?.periodicStats ?? []).length - 1,
});

const isVol = (insight: Insight): boolean =>
  !!(
    insight.trend &&
    insight.trend.periodicStats.length > 0 &&
    insight.trend.periodicStats[0]?.variability
  );

// copied over from python code
// https://github.com/cowpaths/mn/blob/986972606a8db82eb2a584f27dca53c23f5abd73/projects/fullstory/datascience/experiments/descriptive_insights/sim_experiments/trend_insights.py#L85
const getRatioChangeToDisplay = (change?: number): string => {
  if (!change || !isFinite(change)) {
    return '';
  }

  if (change < 2) {
    return convertToPercent(change);
  }

  // if the factor is > 10 then use no decimal places
  if (change >= 10) {
    return intl.formatMessage(
      {
        defaultMessage: '{change, number, ::.}x',
        id: 'mY4u68',
        description:
          'Formatting a number by magnitude ie 200% will go to 2x and 350% will go to 3.5x',
      },
      { change },
    );
  } else {
    return intl.formatMessage(
      {
        defaultMessage: '{change, number, ::.#}x',
        id: 'hoRSOl',
        description:
          'Formatting a number by magnitude ie 200% will go to 2x and 350% will go to 3.5x',
      },
      { change },
    );
  }
};

const getTrendInsightTitle = (insightWords: InsightTrendWords, ratioChange: number): string => {
  if (!isFinite(ratioChange)) {
    // for NaN case
    return intl.formatMessage(
      {
        defaultMessage:
          '{isMonthly, select, true{Monthly} other{Weekly}} {isVolatilityTrend, select, true{volatility} other{average}} value {isIncrease, select, true{increased} other{decreased}} {isIncrease, select, false{to zero} other{above zero}}',
        id: 'Ayrt/0',
      },
      {
        isIncrease: insightWords.isIncrease,
        isMonthly: insightWords.isMonthly,
        isVolatilityTrend: insightWords.isVolatilityTrend,
      },
    );
  }
  return intl.formatMessage(
    {
      defaultMessage:
        '{isMonthly, select, true{Monthly} other{Weekly}} {isVolatilityTrend, select, true{volatility} other{average}} value {isIncrease, select, true{increased} other{decreased}} {ratioChange}',
      id: 'DC9UYo',
    },
    {
      isIncrease: insightWords.isIncrease,
      isMonthly: insightWords.isMonthly,
      isVolatilityTrend: insightWords.isVolatilityTrend,
      ratioChange: insightWords.ratioChangeI18l,
    },
  );
};

const getTrendGroupByInsightTitle = (
  insightWords: InsightTrendGroupByWords,
  featuresDetected: number,
): string => {
  switch (featuresDetected) {
    case FeatureUsed.FEATURES_USED_GAP_CHANGE_ONLY: {
      return intl.formatMessage(
        {
          defaultMessage:
            'Gap value {isIncrease, select, true{increased} other{decreased}} by {ratioChange}',
          id: '5fO0OU',
        },
        {
          isIncrease: insightWords.gapValueIsIncrease,
          ratioChange: insightWords.gapValueRatioChange,
        },
      );
    }
    case FeatureUsed.FEATURES_USED_TOP_VALUE_ONLY: {
      return intl.formatMessage(
        {
          defaultMessage:
            'Top group value {isIncrease, select, true{increased} other{decreased}} by {ratioChange}',
          id: 'MuKRsH',
        },
        {
          isIncrease: insightWords.topValueIsIncrease,
          ratioChange: insightWords.topValueRatioChange,
        },
      );
    }
    case FeatureUsed.FEATURES_USED_TOP_VALUE_AND_GAP_CHANGE:
      return intl.formatMessage(
        {
          defaultMessage:
            ' Leading property average value {topValueIsIncrease, select, true{increased} other{decreased}} {topValueRatioChange}',
          id: 'rqgFYP',
        },
        {
          topValueIsIncrease: insightWords.topValueIsIncrease,
          topValueRatioChange: insightWords.topValueRatioChange,
        },
      );
  }
  return '';
};

export function getInsightTitle(insightWords: InsightWords, insight: Insight) {
  if (isInsightTrendWords(insightWords)) {
    return getTrendInsightTitle(insightWords, insight.trend?.ratioChange ?? 0);
  } else if (isInsightTrendGroupByWords(insightWords)) {
    return getTrendGroupByInsightTitle(insightWords, insight.trendGroup?.featuresDetected ?? 0);
  }
  return '';
}

export function convertToPercent(ratioChange: number) {
  const change = ratioChange > 1 ? ratioChange - 1 : 1 - ratioChange;
  return intl.formatNumber(change, {
    style: 'percent',
    maximumFractionDigits: 1,
    minimumFractionDigits: 0,
  });
}
export function isInsightTrendGroupByDetail(
  insight: InsightDetail,
): insight is InsightTrendGroupByDetail {
  return !!insight.trendGroup;
}

export function isInsightTrendDetail(insight: InsightDetail): insight is InsightTrendDetail {
  return !!insight.trend;
}

export function isInsightTrendWords(words: InsightWords): words is InsightTrendWords {
  return (
    'isVolatilityTrend' in words &&
    'isIncrease' in words &&
    'ratioChangeI18l' in words &&
    'isMonthly' in words
  );
}

export function isInsightTrendGroupByWords(words: InsightWords): words is InsightTrendGroupByWords {
  return 'topValueIsIncrease' in words;
}
